import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react'
import { UseFormReturn } from 'react-hook-form'

import { LuGitPullRequestArrow } from 'react-icons/lu'
import { FormattedMessage } from 'react-intl'

import { Button } from '@/lib/core/components/Button'
import { FeatureModal } from '@/lib/core/components/Modal'
import { SelectOption } from '@/lib/core/components/Select'

import { useShareAssetModalStore } from '@/lib/stores/shareAssetModalStore'

import { ErrorBox } from '@/lib/features/Assets/components/AddAssetModal/AddAssetModal.styles'
import { ContactFormState, ShareFormState } from '@/lib/features/SmartShares/components'
import { SelectContactTo } from '@/lib/features/SmartShares/components/ShareAssetModal/components/SelectContactFor'
import { SelectContactFrom } from '@/lib/features/SmartShares/components/ShareAssetModal/components/SelectContactFrom'
import messages from '@/lib/features/SmartShares/components/ShareAssetModal/ShareAssetModal.messages'
import { getContactStepTitle } from '@/lib/features/SmartShares/components/ShareAssetModal/utils'
import { Share } from '@/lib/features/SmartShares/interfaces'

import { FeatureTypes } from '@/lib/utils/features'

import { Actions, Container } from './ContactSelectModal.styles'

export type ContactSelectModalProps = {
  isActive: boolean
  shareToExtend?: Share
  onClose?: (withSuccess?: boolean) => void
  shareAssetSubmit: (values: ShareFormState) => Promise<void>
  shareAssetValues?: ShareFormState
  setShowContactSelectModal: Dispatch<SetStateAction<boolean>>
  generalError: string
  formMethods: UseFormReturn<ShareFormState, unknown>
  onwardShareToExtend?: Share
}

export const ContactSelectModal = ({
  isActive,
  shareToExtend,
  onClose,
  shareAssetSubmit,
  shareAssetValues,
  setShowContactSelectModal,
  generalError,
  formMethods,
  onwardShareToExtend,
}: ContactSelectModalProps) => {
  const shareClass = useShareAssetModalStore((state) => state.shareClass)
  const [disableSubmit, setDisableSubmit] = useState(false)

  const { formState, getValues, handleSubmit, reset, setValue, watch } = formMethods
  const { isSubmitted, isSubmitting, isValid } = formState

  const currentContactTo = watch('contactTo')
  const currentContactFrom = watch('contactFrom')

  const contactToDefaultValue = useMemo(() => {
    if (shareToExtend?.contactTo) {
      const { id, firstName, lastName } = shareToExtend.contactTo
      return { label: `${firstName} ${lastName}`, value: id } as SelectOption
    }
  }, [shareToExtend, currentContactTo])

  const contactFromDefaultValue = useMemo(() => {
    if (shareToExtend?.contactFrom) {
      const { id, firstName, lastName } = shareToExtend.contactFrom
      return { label: `${firstName} ${lastName}`, value: id } as SelectOption
    }
  }, [shareToExtend])

  const modalTitle = useMemo(
    () =>
      getContactStepTitle({
        isExtend: !!shareToExtend,
        isOnwardShare: !!onwardShareToExtend,
        shareClass,
      }),
    [shareToExtend, onwardShareToExtend, shareClass],
  )

  useEffect(() => {
    const to = getValues('contactTo') != undefined
    const from = getValues('contactFrom') != null

    setDisableSubmit(to !== from)
  }, [currentContactTo, currentContactFrom])

  const onSubmit = async (values: ContactFormState) => {
    if (shareAssetValues) {
      try {
        const payload = {
          organizationTo: shareAssetValues.organizationTo,
          assets: shareAssetValues.assets.map((asset) => {
            return {
              ...asset,
              contactTo: values.contactTo,
              contactFrom: values.contactFrom,
            }
          }),
        } as ShareFormState

        await shareAssetSubmit(payload)
      } catch (error) {
        console.error('Error submitting share asset form:', error)
        // Re-enable the submit button if there's an error
        setDisableSubmit(false)
      }
    }
  }

  useEffect(() => {
    if (shareToExtend?.contactTo && shareToExtend?.contactFrom) {
      setValue('contactTo', shareToExtend.contactTo.id)
      setValue('contactFrom', shareToExtend.contactFrom.id)
    }
  }, [shareToExtend])

  return (
    <FeatureModal
      isActive={isActive}
      feature={FeatureTypes.SHARES}
      title={modalTitle}
      titleIcon={<LuGitPullRequestArrow size={48} />}
      subtitle={<FormattedMessage {...messages.contactSelectSubtitle} />}
      onClose={() => {
        reset()
        setShowContactSelectModal(false)

        if (onClose) {
          onClose()
        }
      }}
    >
      <Container>
        <form
          key={`contact-select-form-${isActive}-${shareToExtend?.id || 'new'}-${onwardShareToExtend?.id || 'none'}`}
          noValidate
          onSubmit={handleSubmit(onSubmit)}
        >
          {isSubmitted && !isValid && (
            <ErrorBox>
              {generalError || <FormattedMessage {...messages.formGeneralError} />}
            </ErrorBox>
          )}
          <SelectContactTo
            shareAssetValues={shareAssetValues}
            defaultValue={contactToDefaultValue}
          />
          <SelectContactFrom defaultValue={contactFromDefaultValue} />
          <Actions>
            <Button
              type="button"
              isLoading={isSubmitting}
              variant="ghost"
              $fitToContent
              size="large"
              minWidth
              onClick={() => setShowContactSelectModal(false)}
            >
              Previous
            </Button>
            <Button
              disabled={disableSubmit}
              type="submit"
              isLoading={isSubmitting}
              variant="secondary"
              $fitToContent
              size="large"
              minWidth
            >
              Share Assets
            </Button>
          </Actions>
        </form>
      </Container>
    </FeatureModal>
  )
}
